import { isEmpty } from 'bold-ui'
import { SimNaoEnum } from 'components/form'
import { intervalToDuration } from 'date-fns'
import { MChatPerguntaEnum, MChatRiscoEnum } from 'graphql/types.generated'
import { FieldMetaState } from 'react-final-form'
import { createValidator, ErrorObject } from 'util/validation'

import { MChatRiscoPrevioRelevanteInfoModel, RegistroMChatQueryModel } from '../mchat/model-mchat'
import { ConsultaSeguimentoPerguntaStatusEnum, PassouFalhouEnum } from './enums-consultaSeguimento'
import {
  CONSULTA_SEGUIMENTO_PERGUNTA_TO_SUB_PERGUNTA,
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoAplicacaoStatsModel,
  ConsultaSeguimentoOutroFieldModel,
  MChatPerguntaPrincipal,
} from './model-consultaSeguimento'

const MCHAT_PERGUNTAS_ARRAY = Object.keys(MChatPerguntaEnum) as MChatPerguntaEnum[]

export function calculateResultadoConsultaSeguimento(aplicacao: ConsultaSeguimentoAplicacaoModel): MChatRiscoEnum {
  let resultado: MChatRiscoEnum
  const values = Object.values(aplicacao)
  const perguntasFalhou = values.filter((value) => value.resultado === PassouFalhouEnum.FALHOU)

  if (perguntasFalhou.length >= 2) {
    resultado = MChatRiscoEnum.POSITIVO
  } else {
    resultado = MChatRiscoEnum.NEGATIVO
  }

  return resultado
}

export function getPerguntasSelecionadasFieldHasError(
  meta: Pick<FieldMetaState<MChatPerguntaEnum[]>, 'error' | 'touched'>,
  isMChatRiscoBaixo: boolean
): boolean {
  const { error, touched } = meta

  const hasErrorFromForm = !isEmpty(error) && touched
  const hasErrorFromMChat = !isEmpty(error) && isMChatRiscoBaixo
  const hasError = hasErrorFromForm || hasErrorFromMChat

  return hasError
}

export function getModalFooterCounterText(numberOfPerguntasSelecionadas: number, hasError: boolean): string {
  const hasPerguntasSelecionadas = numberOfPerguntasSelecionadas > 0

  const filledText = numberOfPerguntasSelecionadas === 1 ? 'pergunta selecionada' : 'perguntas selecionadas'
  const emptyText = 'Nenhuma pergunta selecionada'

  const emptyErrorText = `${emptyText}. Selecione mais perguntas.`
  const filledErrorText = `${filledText}. Selecione mais perguntas.`

  const validatedFilledText = hasError ? filledErrorText : filledText
  const validatedEmptyText = hasError ? emptyErrorText : emptyText

  return hasPerguntasSelecionadas ? validatedFilledText : validatedEmptyText
}

export const hasAnyNao = <T extends string>(value: Record<T, SimNaoEnum> | undefined | null): boolean => {
  if (!value || isEmpty(value)) return false
  return Object.values(value).some((item) => item === SimNaoEnum.NAO)
}

export const hasAnySim = <T extends string>(value: Record<T, SimNaoEnum | undefined> | undefined | null): boolean => {
  if (!value || isEmpty(value)) return false
  return Object.values(value).some((item) => item === SimNaoEnum.SIM)
}

export const isAllNao = <T extends string>(
  value: Partial<Record<T, SimNaoEnum | undefined>> | undefined | null,
  expected: T[]
): boolean => {
  if (!value || !expected || isEmpty(expected)) return false

  return expected.every((campo) => value[campo] === SimNaoEnum.NAO)
}

export const isAllSim = <T extends string>(
  value: Partial<Record<T, SimNaoEnum | undefined>> | undefined | null,
  expected: T[]
): boolean => {
  if (!value || !expected || isEmpty(expected)) return false

  return expected.every((campo) => value[campo] === SimNaoEnum.SIM)
}

export const isAllFilled = <T extends string>(
  value: Partial<Record<T, SimNaoEnum | undefined>> | undefined | null,
  expected: T[]
): boolean => {
  if (!value || !expected || isEmpty(expected)) return false

  return expected.every((campo) => value[campo] === SimNaoEnum.NAO || value[campo] === SimNaoEnum.SIM)
}

export const isPerguntaOutroFilled = (value: ConsultaSeguimentoOutroFieldModel | undefined | null): boolean => {
  return !isEmpty(value?.textValue)
}

export const isPerguntaOutroSelected = (value: ConsultaSeguimentoOutroFieldModel | undefined | null): boolean => {
  return !isEmpty(value?.radioValue)
}

export const isPerguntaOutroSim = (value: ConsultaSeguimentoOutroFieldModel | undefined | null): boolean => {
  return !isEmpty(value?.textValue) && value?.radioValue === SimNaoEnum.SIM
}

export const isPerguntaOutroNao = (value: ConsultaSeguimentoOutroFieldModel | undefined | null): boolean => {
  return !isEmpty(value?.textValue) && value?.radioValue === SimNaoEnum.NAO
}

export function getMChatForConsultaSeguimento(
  mChatAtualRisco: MChatRiscoEnum | undefined,
  mChatAnteriorRisco: MChatRiscoEnum | undefined,
  mChatAtualPerguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>,
  mChatAnteriorPerguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>
): {
  risco: MChatRiscoEnum | undefined
  perguntasFalhas: ReadonlyArray<MChatPerguntaPrincipal>
} {
  if (mChatAtualRisco && !isEmpty(mChatAtualRisco)) {
    return {
      risco: mChatAtualRisco,
      perguntasFalhas: mChatAtualPerguntasFalhas,
    }
  }
  return {
    risco: mChatAnteriorRisco,
    perguntasFalhas: mChatAnteriorPerguntasFalhas,
  }
}

export function getResultadoLabel(
  aplicacaoStats: ConsultaSeguimentoAplicacaoStatsModel,
  isConsultaSeguimentoDesatualizada: boolean = false
): string {
  if (isConsultaSeguimentoDesatualizada) {
    return `Passou: -/- perguntas | Falhou: -/- perguntas`
  }
  return `Passou: ${aplicacaoStats.totalPerguntasPassou}/${aplicacaoStats.totalPerguntasRespondidas} perguntas | Falhou: ${aplicacaoStats.totalPerguntasFalhou}/${aplicacaoStats.totalPerguntasRespondidas} perguntas`
}

export function getPerguntaPaiBySubPergunta(subPergunta: MChatPerguntaEnum): MChatPerguntaEnum | undefined {
  return MCHAT_PERGUNTAS_ARRAY.find((pergunta) =>
    CONSULTA_SEGUIMENTO_PERGUNTA_TO_SUB_PERGUNTA[pergunta]?.has(subPergunta)
  )
}

export const isStatusValid = (value: ConsultaSeguimentoPerguntaStatusEnum) => {
  if (
    ![
      ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU,
      ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU,
      ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU,
      ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU,
    ].includes(value)
  ) {
    return 'Status inválido'
  }
}

export const validatorConsultaSeguimentoOutro = createValidator<ConsultaSeguimentoOutroFieldModel>(
  {},
  (values: ConsultaSeguimentoOutroFieldModel, errors: ErrorObject<ConsultaSeguimentoOutroFieldModel>) => {
    if (!isEmpty(values?.textValue) && isEmpty(values?.radioValue)) {
      errors.textValue = 'É necessário responder sim ou não para continuar.'
    }
    return errors
  }
)

export function getAlertaConsultaSeguimentoAnterior(
  resultadoAtual: MChatRiscoEnum | undefined | null,
  serverTimeNow: Date,
  registrosMChat: ReadonlyArray<RegistroMChatQueryModel> | undefined | null = []
): MChatRiscoPrevioRelevanteInfoModel | null {
  if (!resultadoAtual) return null

  const registrosMchatRf = (registrosMChat ?? []).filter((registro) => registro.isMchatRf === true)

  const ultimoRiscoPositivo = registrosMchatRf
    .filter((r) => r.risco === MChatRiscoEnum.POSITIVO)
    .sort((a, b) => new Date(b.dataResultado).getTime() - new Date(a.dataResultado).getTime())
    .first()

  if (ultimoRiscoPositivo) {
    return {
      risco: MChatRiscoEnum.POSITIVO,
      duracaoDesdeRiscoPrevio: intervalToDuration({
        start: new Date(ultimoRiscoPositivo.dataResultado),
        end: serverTimeNow,
      }),
    }
  }

  return null
}
